import { useEffect, useState } from 'react';

import { formatCountdown } from '@/utils/formatCountdown';
import type { LotteryWinnerRow } from '@/types/schemas';

interface LotteryWarmupViewProps {
  seasonNo: number;
  startTime: string;
  unclaimedWinner?: LotteryWinnerRow | null;
  anyWinner?: LotteryWinnerRow | null; // Claimed or unclaimed winner for display
  previousSeasonNo?: number;
  hasParticipatedBefore?: boolean;
  onViewWinners: () => void;
  onClaimReward: () => void;
  onMintNFT?: () => void;
  nftAlreadyMinted?: boolean;
}

export function LotteryWarmupView({
  seasonNo,
  startTime,
  unclaimedWinner,
  anyWinner,
  hasParticipatedBefore,
  onViewWinners,
  onClaimReward,
  onMintNFT,
  nftAlreadyMinted,
}: LotteryWarmupViewProps) {
  const [countdown, setCountdown] = useState('--');

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(formatCountdown(startTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Determine if user is a winner or loser
  // Use anyWinner (which includes claimed winners) to determine winner state
  const isWinner = anyWinner !== null && anyWinner !== undefined;
  const isLoser = hasParticipatedBefore && !anyWinner;
  const isNonParticipant = !hasParticipatedBefore && !anyWinner;

  return (
    <div className="max-w-3xl mx-auto px-4">
      {/* Season Start Countdown Header */}
      <div className="text-center mb-4">
        <h1
          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl uppercase"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 700,
            color: '#FFFFFF',
          }}
        >
          Season {seasonNo} Start{' '}
          <span
            style={{
              background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {countdown}
          </span>
        </h1>
      </div>

      {/* Placeholder Image */}
      <div className="flex justify-center -my-2 px-2">
        <div className="relative w-full max-w-2xl flex items-center justify-center">
          <img
            src={isNonParticipant ? "/loser.svg" : "/winner.svg"}
            alt="Season Results"
            className="w-full h-auto object-contain"
            onError={(e) => {
              // Fallback if image doesn't exist yet
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.nextElementSibling;
              if (placeholder) (placeholder as HTMLElement).style.display = 'flex';
            }}
          />
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ display: 'none' }}
          >
            <span className="text-6xl opacity-30">{isNonParticipant ? '😔' : isLoser ? '🎉' : '🏆'}</span>
          </div>
        </div>
      </div>

      {/* Message */}
      {isLoser ? (
        <div className="space-y-2 mt-0 px-2">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase text-center"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            CONGRATULATIONS
          </h2>
          <p
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white text-center"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
          >
            You earned a Seasonal NFT!
          </p>
        </div>
      ) : isWinner ? (
        <div className="space-y-2 mt-0 px-2">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase text-center"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            CONGRATULATIONS
          </h2>
          <p
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white text-center"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
          >
            You've won the previous lottery draw.
          </p>
        </div>
      ) : (
        <div className="space-y-2 mt-0 px-2">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase text-center"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            Whoops
          </h2>
          <p
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white text-center"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400 }}
          >
            You didn't invest any points this time! Participate next time to join the fun!
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-4 mt-6 px-4">
        {isLoser ? (
          <>
            {/* Mint NFT Button for participants */}
            {onMintNFT && !nftAlreadyMinted && (
              <button
                onClick={onMintNFT}
                className="w-full max-w-md py-3 sm:py-4 uppercase tracking-wide text-2xl sm:text-3xl transition-all hover:scale-[1.02]"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontWeight: 700,
                  backgroundColor: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '100px',
                  border: '5px solid #50E4F9',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                }}
              >
                Mint NFT
              </button>
            )}

            {nftAlreadyMinted && (
              <div className="text-center text-sm text-white/60" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ✅ NFT Already Minted
              </div>
            )}

            <button
              onClick={onViewWinners}
              className="text-white underline transition-all hover:opacity-70"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              View Winner List
            </button>
          </>
        ) : isWinner ? (
          <>
            <button
              onClick={onClaimReward}
              className="w-full max-w-md py-3 sm:py-4 rounded-full uppercase tracking-wide text-2xl sm:text-3xl transition-all hover:scale-[1.02]"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                fontWeight: 700,
                background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                color: '#000000',
                boxShadow: '0 8px 32px rgba(251, 139, 0, 0.4)',
              }}
            >
              {unclaimedWinner ? 'Claim Your Reward' : 'View Your Reward'}
            </button>

            {/* Mint NFT Button for winners */}
            {onMintNFT && !nftAlreadyMinted && (
              <button
                onClick={onMintNFT}
                className="w-full max-w-md py-3 sm:py-4 uppercase tracking-wide text-2xl sm:text-3xl transition-all hover:scale-[1.02]"
                style={{
                  fontFamily: 'Hyperspace Race Capsule, cursive',
                  fontWeight: 700,
                  backgroundColor: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '100px',
                  border: '5px solid #50E4F9',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                }}
              >
                Mint NFT
              </button>
            )}

            {nftAlreadyMinted && (
              <div className="text-center text-sm text-white/60" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ✅ NFT Already Minted
              </div>
            )}

            <button
              onClick={onViewWinners}
              className="text-white underline transition-all hover:opacity-70"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              View Winner List
            </button>
          </>
        ) : (
          <>
            {/* Non-participant state - only show View Winners button */}
            <button
              onClick={onViewWinners}
              className="text-white underline transition-all hover:opacity-70"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              View Winner List
            </button>
          </>
        )}
      </div>
    </div>
  );
}
